/**
 * Mobile-only, fixed-to-bottom address-entry bar that replaces the
 * BallotSheet in its previewClosed state (guest + no saved address +
 * new chat page + keyboard down).
 *
 * Why this exists separately from ``BallotSheet`` at previewClosed:
 * the sheet UX (teal grabber + label + drag-to-open) implies "there's
 * a ballot below — drag to see it". A guest without an address has
 * nothing to drag to, so the sheet framing is misleading. This
 * component strips back to just the locality label + address input —
 * the only thing the user CAN act on in this state.
 *
 * Behavior:
 *   - Bottom-anchored, lifts above the on-screen keyboard via the
 *     same ``env(keyboard-inset-height)`` / ``--keyboard-inset-px``
 *     fallback the chat input uses.
 *   - No drag handlers, no grabber pill, no ballot label.
 *   - Saving an address transitions out of the previewClosed state
 *     (``useGuestAddress`` writes localStorage, the ChatWindow effect
 *     re-evaluates the previewClosed condition, this bar unmounts, and
 *     the BallotSheet re-appears with a tappable grabber).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useBallotSheet } from '@/providers/BallotSheetProvider';
import useGuestAddress from '@/hooks/useGuestAddress';
import useAnalytics from '@/hooks/useAnalytics';
import useApi from '@/hooks/useApi';
import { useAuth } from '@/components/auth/AuthProvider';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import { useResearch } from '@/contexts/ResearchContext';
import { resolveAddressFromText } from '@/utils/placesApi';
import { primitiveColors, semanticColors, BORDER_RADIUS, BORDER_RADIUS_LG } from '@/theme/tokens';
import AutoCompleteAddressInput from '../signup/AutoCompleteAddressInput';

// Tracks the ?address= value prefilled this page load to prevent re-submit on remount.
let prefilledAddressThisLoad: string | null = null;

interface AddressDetails {
  formatted_address: string;
  place_id: string;
  address_components: unknown[];
  geometry: { location: { lat: number; lng: number } };
}

const MobileAddressBar: React.FC = () => {
  // ``closedDetentFrac > 0`` <=> the provider is in previewClosed mode.
  // The only consumer that sets ``setPreviewClosed(true)`` is the same
  // guest-no-address-new-chat condition we want this bar to cover, so
  // the value doubles as a gate.
  const { t } = useTranslation('ballot');
  const { closedDetentFrac, snapTo, registerAddressInputFocuser } = useBallotSheet();
  const isActive = closedDetentFrac > 0;

  const { setAddress } = useGuestAddress();
  const { track } = useAnalytics();
  const { isAuthenticated, user } = useAuth();
  const query = useApi();
  const { beginBuilding, refreshRecords } = useBallotRecords();
  const { address: urlAddress } = useResearch();
  const [inputValue, setInputValue] = useState('');
  const barRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const prefillStartedRef = useRef(false);
  const authedSubmitRef = useRef(false);

  // Register a focus callback with the provider so the LeftPaneMenu's
  // "My Ballot Preview" item can focus this input from afar when the
  // bar is active (and there's no sheet to open).
  useEffect(() => {
    if (!isActive) return undefined;
    registerAddressInputFocuser(() => addressInputRef.current?.focus());
    return () => registerAddressInputFocuser(null);
  }, [isActive, registerAddressInputFocuser]);

  // When the bar transitions out (address saved), clear the local input
  // value so a future re-entry to this state starts empty rather than
  // showing the prior text.
  useEffect(() => {
    if (!isActive) setInputValue('');
  }, [isActive]);

  // Expose the bar's measured height as a CSS variable so consumers
  // higher in the layout (the chat scroll container's sticky bottom)
  // can pad themselves above the bar instead of being overlapped.
  // ResizeObserver picks up content changes (e.g., the address row
  // wrapping). Variable is cleared on unmount so consumers fall back
  // to 0px when the bar isn't shown.
  useEffect(() => {
    if (!isActive) return undefined;
    const el = barRef.current;
    if (!el) return undefined;
    const setVar = () => {
      document.documentElement.style.setProperty(
        '--mobile-address-bar-height',
        `${el.offsetHeight}px`
      );
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--mobile-address-bar-height');
    };
  }, [isActive]);

  const handleSelect = useCallback(
    (details: AddressDetails) => {
      // Signed-in user who deleted their last ballot: persist the address to
      // their profile and rebuild the real ballot (mirrors the desktop
      // landing's submit). Refreshing records repopulates the sheet.
      if (isAuthenticated) {
        if (authedSubmitRef.current) return;
        authedSubmitRef.current = true;
        // Enter the building state + open the sheet so the loading skeleton
        // shows while the settings write does its Democracy Works round-trip.
        beginBuilding();
        snapTo('mid');
        track('Landing See Ballot Clicked', { via: 'mobile_bar', authed: true });
        void (async () => {
          try {
            await query('/user/settings', {
              method: 'POST',
              body: JSON.stringify({
                email: user?.email,
                address_payload: {
                  formatted_address: details.formatted_address,
                  address_components: details.address_components,
                },
              }),
            });
            await refreshRecords();
          } catch {
            await refreshRecords();
          } finally {
            authedSubmitRef.current = false;
          }
        })();
        return;
      }
      setAddress({
        formatted_address: details.formatted_address,
        address_components: details.address_components as unknown[],
      });
      // Guest first-address moment. ``isGuest`` / ``platform`` are stamped as
      // Mixpanel super properties in App.tsx (MixpanelSuperPropertiesRegistrar).
      track('Guest Chat Address Added', {
        source: 'Mobile Address Bar',
      });
      // Address now in sessionStorage → useGuestAddress notifies subscribers →
      // ChatWindow's previewClosed effect re-runs and clears it → this bar
      // unmounts. Snap the sheet to mid so the personalized ballot slides in.
      snapTo('mid');
    },
    [isAuthenticated, user?.email, query, beginBuilding, refreshRecords, setAddress, snapTo, track]
  );

  // One-shot prefill trigger: fires when bar becomes active and a ?address= param is present.
  useEffect(() => {
    if (!isActive || !urlAddress) return;
    if (prefillStartedRef.current) return;
    if (prefilledAddressThisLoad === urlAddress) return; // already handled this load (e.g. remount)
    if (inputValue.trim().length > 0) return; // user already typing

    prefillStartedRef.current = true;
    prefilledAddressThisLoad = urlAddress;
    setInputValue(urlAddress);
    void resolveAddressFromText(urlAddress).then((details) => {
      if (details) {
        track('Landing Address Prefilled', { source: 'research_link' });
        handleSelect({
          formatted_address: details.formatted_address,
          place_id: details.place_id,
          address_components: details.address_components,
          geometry: details.geometry,
        });
      } else {
        track('Landing Address Prefill Failed', { source: 'research_link' });
        // Leave the address in the input so the user can pick from autocomplete.
      }
    });
    // ``inputValue`` excluded so this stays one-shot rather than re-firing on typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, urlAddress, handleSelect, track]);

  if (!isActive) return null;

  return (
    <Box
      ref={barRef}
      sx={{
        display: { xs: 'block', md: 'none' },
        position: 'fixed',
        left: 0,
        right: 0,
        // Sit above whichever of the keyboard / safe-area inset is
        // currently in effect — but not both stacked. The keyboard,
        // when up, physically covers the safe-area region (gesture
        // nav bar on Android, home indicator on iOS).
        //
        // ``--keyboard-inset-px`` is the single source of truth
        // maintained by ``BallotSheetProvider`` — vk.boundingRect on
        // Chromium, visualViewport fallback on iOS Safari, with the
        // Edge Android bottom-toolbar overshoot already subtracted
        // at the source. Consumers just read this var; no per-
        // browser handling needed here.
        bottom: 'max(var(--keyboard-inset-px, 0px), env(safe-area-inset-bottom))',
        backgroundColor: semanticColors.ballotElectionCard,
        // Just above the BallotSheet (modal + 1) so this bar wins in
        // any z-stacking against it during the brief frame where both
        // might be visible.
        zIndex: (theme) => theme.zIndex.modal + 1,
        px: 1,
        // Padding above the locality label.
        pt: '10px',
        pb: 1,
        // Same rounded top corners as the BallotSheet — visually
        // suggests "this slides up from below" even though there's no
        // drag affordance.
        borderTopLeftRadius: BORDER_RADIUS_LG,
        borderTopRightRadius: BORDER_RADIUS_LG,
        boxShadow: `0 -2px 16px ${semanticColors.shadowLight}`,
      }}
    >
      {/* Locality prompt — same copy as the in-sheet header. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1,
          px: 0.5,
        }}
      >
        <LocationOnIcon sx={{ color: primitiveColors.white, fontSize: 16 }} aria-hidden="true" />
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            lineHeight: 1.2,
            color: primitiveColors.white,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {t('header.nationalLocality')}
        </Typography>
      </Box>

      {/* Address input — same component the sheet uses internally. No
          onClick/onFocus interceptor here: we WANT the default focus
          behavior (open keyboard) since the bar's ``bottom`` anchor
          will lift it above the keyboard. No snap-to-mid because
          there's no sheet to snap. */}
      <AutoCompleteAddressInput
        value={inputValue}
        onChange={setInputValue}
        onAddressSelect={handleSelect}
        placeholder={t('address.placeholder')}
        size="small"
        inputRef={addressInputRef}
        // The bar sits directly above the keyboard. A below-dropdown
        // would be entirely covered by the keyboard — flip it above
        // the input so suggestions are tappable.
        dropdownDirection="above"
        sx={{
          background: 'transparent',
          '& .MuiOutlinedInput-root': {
            borderRadius: BORDER_RADIUS,
            fontSize: '16px',
            backgroundColor: primitiveColors.white,
          },
        }}
      />
    </Box>
  );
};

export default MobileAddressBar;
